import React, { useEffect, useMemo, useState } from "react";
import { Grid, Card, CardContent, Typography, CircularProgress, Button, TextField, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { useNavigate } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { keyframes } from "@mui/system";
import Navbar from "./Navbar";
import Logger from "./Logger";
import { getLoggerBaseURL } from "../utils/getLoggerBaseURL";

const bounceAnimation = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(4px); }
  100% { transform: translateY(0); }
`;

const PackerOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [visibleOrders, setVisibleOrders] = useState(6);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const navigate = useNavigate();
    const adminEmail = sessionStorage.getItem("adminEmail");
    const logger = useMemo(() => new Logger(getLoggerBaseURL()), []);

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                const response = await fetch(`/api/packer/getSummary/${adminEmail}`);

                if (!response.ok) {
                    throw new Error("Failed to fetch orders");
                }

                const data = await response.json();
                setOrders(data.OrderNumbers || []);
            } catch (error) {
                logger.log(error.message, "ERROR");
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, [adminEmail, logger]);

    const filteredOrders = orders.filter((order) =>
        order?.Order_No?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleAccept = (order) => {
        setSelectedOrder(order);
        setOpenConfirmDialog(true);
    };

    const handleConfirmAccept = () => {
        sessionStorage.setItem("type", "Order");
        sessionStorage.setItem("orderNo", selectedOrder.Order_No);
        navigate("/packageDetails");
    };

    if (loading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", flexDirection: "column" }}            >
                <CircularProgress />
                <Typography sx={{ marginTop: 2 }}>
                    Fetching orders...
                </Typography>
            </div>
        );
    }

    return (
        <div>
            <Navbar />
            <div style={{ background: "#f5f7fb", minHeight: "100vh", padding: "20px" }} >
                {/* Header */}
                <div style={{ marginBottom: "20px", marginTop: "50px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "15px" }} >
                    <Typography sx={{ fontWeight: "bold", fontSize: "25px", lineHeight: "40px", color: "#111827" }} >
                        Orders
                    </Typography>

                    <TextField
                        size="small"
                        placeholder="Search orders..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{
                            background: "white",
                            minWidth: "260px",
                            borderRadius: "10px",
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                            },
                        }}
                    />
                </div>

                {/* Orders Grid */}
                <Grid container spacing={3}>
                    {filteredOrders
                        .slice(0, visibleOrders)
                        .map((order, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Card
                                    sx={{
                                        borderRadius: "18px",
                                        border: "1px solid #e5e7eb",
                                        boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                                        height: "100%",
                                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                                        "&:hover": {
                                            transform: "translateY(-3px)",
                                            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                                        },
                                    }}
                                >
                                    <CardContent sx={{ padding: "22px", display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%" }} >
                                        <div>
                                            <Typography sx={{ fontSize: "13px", color: "#6b7280", marginBottom: "6px" }} >
                                                Order Number
                                            </Typography>

                                            <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#111827" }} >
                                                {order.Order_No}
                                            </Typography>

                                            <div style={{ marginTop: "18px" }} >
                                                <Typography sx={{ fontSize: "13px", color: "#6b7280" }} >
                                                    Company
                                                </Typography>

                                                <Typography sx={{ fontSize: "15px", fontWeight: 500, color: "#374151", marginTop: "4px" }} >
                                                    {order.Comp_Name || "N/A"}
                                                </Typography>
                                            </div>
                                        </div>

                                        <Button
                                            onClick={() =>
                                                handleAccept(order)
                                            }
                                            variant="contained"
                                            sx={{
                                                marginTop: "24px",
                                                backgroundColor: "#1a7f22",
                                                color: "white",
                                                borderRadius: "10px",
                                                textTransform: "none",
                                                height: "42px",
                                                fontWeight: 600,
                                                boxShadow: "none",
                                                "&:hover": {
                                                    backgroundColor: "#166534",
                                                    boxShadow: "none",
                                                },
                                            }}
                                        >
                                            Accept Order
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                </Grid>

                {/* Load More */}
                {visibleOrders < filteredOrders.length && (
                    <Button
                        onClick={() =>
                            setVisibleOrders((prev) => prev + 6)
                        }
                        sx={{
                            margin: "35px auto 10px auto",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            background: "white",
                            border: "1px solid #d1d5db",
                            color: "#374151",
                            borderRadius: "12px",
                            textTransform: "none",
                            padding: "10px 18px",
                            fontWeight: 600,
                            "&:hover": {
                                background: "#f9fafb",
                                border: "1px solid #9ca3af",
                            },
                        }}
                    >
                        Load More Orders

                        <KeyboardArrowDownIcon sx={{ animation: `${bounceAnimation} 1s infinite` }} />
                    </Button>
                )}
            </div>

            {/* Confirmation Dialog */}
            <Dialog
                open={openConfirmDialog}
                onClose={() => setOpenConfirmDialog(false)}
                PaperProps={{
                    sx: { borderRadius: "16px", padding: "10px" },
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: "20px" }} >
                    Confirmation
                </DialogTitle>

                <DialogContent>
                    <DialogContentText sx={{ color: "#4b5563" }} >
                        Are you sure you want to accept this order?
                    </DialogContentText>
                </DialogContent>

                <DialogActions sx={{ padding: "0 20px 20px 20px" }}>
                    <Button
                        onClick={() => setOpenConfirmDialog(false)}
                        sx={{ textTransform: "none", color: "#6b7280" }} >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleConfirmAccept}
                        sx={{
                            background: "#1a7f22",
                            textTransform: "none",
                            borderRadius: "10px",
                            boxShadow: "none",
                            "&:hover": {
                                background: "#166534",
                                boxShadow: "none",
                            },
                        }}
                    >
                        Accept
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default PackerOrders;